import { useMemo, useState } from 'react';
import { Button, Drawer, Form, InputNumber, Tooltip } from 'antd';
import {
  CheckOutlined,
  CloseCircleFilled,
  UndoOutlined,
} from '@ant-design/icons';
import '../CapacityButtons.less';
import '../CapacityDrawer.less';
import {
  BulkClusterSizeUpdateResult,
  useBulkUpdateClusterSizes,
} from 'hooks/capacityManagementQueries';
import { ClusterSizesPerClusterCodeItem } from 'types/capacityManagement.types';
import { displayErrorMessage, displaySuccessMessage } from 'utils';
import {
  formatMemory,
  resourcesOf,
  sameResources,
  SizeResources,
} from '../capacitySummary';
import AffectedClusters from '../AffectedClusters/AffectedClusters';

export type BulkEditTarget = {
  size: string;
  baseline?: SizeResources;
  cells: { clusterCode: string; cell: ClusterSizesPerClusterCodeItem }[];
  // Start the form at the baseline (the "Reset to baseline" action) instead of the shared value.
  startFromBaseline?: boolean;
};

type FieldKey = keyof SizeResources;

type DraftValues = Record<FieldKey, number | null>;

const FIELDS: { key: FieldKey; label: string; unit: string }[] = [
  { key: 'memoryRequest', label: 'Memory request', unit: 'GB' },
  { key: 'memoryLimit', label: 'Memory limit', unit: 'GB' },
  { key: 'cpu', label: 'CPU', unit: 'vCPU' },
];

function initialDraft(target: BulkEditTarget): DraftValues {
  if (target.startFromBaseline && target.baseline)
    return { ...target.baseline };
  const current = target.cells.map(({ cell }) => resourcesOf(cell));
  const shared = (key: FieldKey) =>
    current.every(value => value[key] === current[0]?.[key])
      ? (current[0]?.[key] ?? null)
      : null;
  return {
    memoryRequest: shared('memoryRequest'),
    memoryLimit: shared('memoryLimit'),
    cpu: shared('cpu'),
  };
}

function currentRange(values: number[]) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max ? `Now ${min} on all` : `Now ${min}–${max}, mixed`;
}

function BulkEditDrawer({
  target,
  canEdit,
  onClose,
}: Readonly<{
  target: BulkEditTarget;
  canEdit: boolean;
  onClose: () => void;
}>) {
  const [draft, setDraft] = useState<DraftValues>(() => initialDraft(target));
  const [results, setResults] = useState<BulkClusterSizeUpdateResult[]>([]);
  const { mutateAsync: bulkUpdate, isPending } = useBulkUpdateClusterSizes();

  const preview = useMemo(
    () =>
      target.cells.map(({ clusterCode, cell }) => {
        const current = resourcesOf(cell);
        const next: SizeResources = {
          memoryRequest: draft.memoryRequest ?? current.memoryRequest,
          memoryLimit: draft.memoryLimit ?? current.memoryLimit,
          cpu: draft.cpu ?? current.cpu,
        };
        return {
          clusterCode,
          cell,
          current,
          next,
          changed: !sameResources(current, next),
          invalid: next.memoryRequest > next.memoryLimit,
          error: results.find(result => result.sizeID === cell.id)?.error,
        };
      }),
    [draft, results, target.cells],
  );

  const changedRows = preview.filter(row => row.changed);
  const hasInvalid = changedRows.some(row => row.invalid);
  const saveDisabled = !canEdit || changedRows.length === 0 || hasInvalid;
  const operatorWord = (count: number) =>
    `${count} operator${count === 1 ? '' : 's'}`;

  async function handleSave() {
    // Each PUT carries the cell's own node label: the backend checks the new memory and CPU
    // against those node pools, and leaves roles, over-commitment and Istio untouched.
    const outcome = await bulkUpdate(
      changedRows.map(({ clusterCode, cell, next }) => ({
        sizeID: cell.id,
        clusterCode,
        updatedClusterSize: {
          memoryRequest: next.memoryRequest,
          memoryLimit: next.memoryLimit,
          memoryText: next.memoryLimit,
          cpu: next.cpu,
          nodeLabel: cell.nodeLabelObj,
        },
      })),
    );
    const failed = outcome.filter(result => result.error);
    setResults(outcome);
    if (failed.length === 0) {
      displaySuccessMessage(
        `Updated ${target.size} on ${operatorWord(outcome.length)}`,
      );
      onClose();
    } else {
      displayErrorMessage(
        `${operatorWord(failed.length)} could not be updated. See the details below.`,
      );
    }
  }

  return (
    <Drawer
      open
      width={600}
      onClose={onClose}
      className="capacity-drawer"
      title={
        <div className="capacity-drawer__title">
          <span className="capacity-drawer__eyebrow">Edit size</span>
          <span>
            <span className="capacity-drawer__mono">{target.size}</span> on{' '}
            {operatorWord(target.cells.length)}
          </span>
        </div>
      }
      footer={
        <div className="capacity-drawer__footer">
          <Button
            className="capacity-btn capacity-btn--lg capacity-btn--outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Tooltip
            title={canEdit ? undefined : 'You don’t have sufficient permission'}
          >
            <Button
              className="capacity-btn capacity-btn--lg capacity-btn--primary"
              icon={<CheckOutlined aria-hidden="true" />}
              loading={isPending}
              disabled={saveDisabled}
              onClick={handleSave}
            >
              Save to {operatorWord(changedRows.length)}
            </Button>
          </Tooltip>
        </div>
      }
    >
      <div className="capacity-drawer__body">
        <section className="capacity-drawer__section">
          <h3 className="capacity-drawer__heading">Applies to</h3>
          <div className="capacity-drawer__chips">
            {target.cells.map(({ clusterCode }) => (
              <span key={clusterCode} className="capacity-drawer__chip">
                {clusterCode}
              </span>
            ))}
          </div>
        </section>

        <section className="capacity-drawer__section">
          <div className="capacity-drawer__heading-row">
            <h3 className="capacity-drawer__heading">Resources</h3>
            {target.baseline && (
              <Button
                className="capacity-btn capacity-btn--sm capacity-btn--subtle"
                icon={<UndoOutlined aria-hidden="true" />}
                onClick={() => setDraft({ ...target.baseline! })}
              >
                Fill with baseline · {formatMemory(target.baseline)} ·{' '}
                {target.baseline.cpu} vCPU
              </Button>
            )}
          </div>
          <Form layout="vertical" className="capacity-drawer__fields">
            {FIELDS.map(field => (
              <Form.Item
                key={field.key}
                label={`${field.label} (${field.unit})`}
                htmlFor={`bulk-edit-${field.key}`}
                extra={`${currentRange(
                  target.cells.map(({ cell }) => resourcesOf(cell)[field.key]),
                )}. Leave empty to keep each value.`}
              >
                <InputNumber
                  id={`bulk-edit-${field.key}`}
                  min={1}
                  placeholder="Keep each"
                  value={draft[field.key]}
                  onChange={value =>
                    setDraft(prev => ({
                      ...prev,
                      [field.key]: typeof value === 'number' ? value : null,
                    }))
                  }
                />
              </Form.Item>
            ))}
          </Form>
          <p className="capacity-drawer__hint">
            Over-commitment, roles, node labels and Istio settings stay as they
            are. Change them per operator with Edit.
          </p>
        </section>

        <section className="capacity-drawer__section">
          <h3 className="capacity-drawer__heading">What will change</h3>
          <table className="capacity-drawer__preview">
            <thead>
              <tr>
                <th scope="col">Operator</th>
                <th scope="col">Memory req / limit</th>
                <th scope="col">CPU</th>
              </tr>
            </thead>
            <tbody>
              {preview.map(row => (
                <tr
                  key={row.clusterCode}
                  className={
                    row.invalid || row.error
                      ? 'capacity-drawer__preview-row--error'
                      : undefined
                  }
                >
                  <th scope="row" className="capacity-drawer__mono">
                    {row.clusterCode}
                  </th>
                  <td>
                    {sameMemory(row.current, row.next) ? (
                      <span className="capacity-drawer__muted">
                        {formatMemory(row.current)}, unchanged
                      </span>
                    ) : (
                      <>
                        <s className="capacity-drawer__muted">
                          {formatMemory(row.current)}
                        </s>{' '}
                        → <b>{formatMemory(row.next)}</b>
                      </>
                    )}
                    {row.invalid && (
                      <span className="capacity-drawer__error">
                        Request is above the limit
                      </span>
                    )}
                    {row.error && (
                      <span className="capacity-drawer__error">
                        <CloseCircleFilled aria-hidden="true" /> {row.error}
                      </span>
                    )}
                  </td>
                  <td>
                    {row.current.cpu === row.next.cpu ? (
                      <span className="capacity-drawer__muted">
                        {row.current.cpu}, unchanged
                      </span>
                    ) : (
                      <>
                        <s className="capacity-drawer__muted">
                          {row.current.cpu}
                        </s>{' '}
                        → <b>{row.next.cpu}</b>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {changedRows.length > 0 && (
          <AffectedClusters
            sizeIDs={changedRows.map(row => row.cell.id)}
            sizeName={target.size}
          />
        )}
      </div>
    </Drawer>
  );
}

function sameMemory(a: SizeResources, b: SizeResources) {
  return a.memoryRequest === b.memoryRequest && a.memoryLimit === b.memoryLimit;
}

export default BulkEditDrawer;
